import { useCallback, useState } from "react";
import { createPortal } from "react-dom";

import { Backdrop } from "@internal/backstage-plugin-utility-react";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";

import { useEscapeKey, useIsAuthenticated, usePortalContainer } from "../hooks";
import { ChatPanel } from "./ChatPanel";
import styles from "./ChatWidget.module.css";

// ── ChatWidget ─────────────────────────────────────────────────────────────────

export const ChatWidget = () => {
    const { isAuthenticated, authError, retry } = useIsAuthenticated();
    const container = usePortalContainer();

    if (!container) return null;
    if (!isAuthenticated && !authError) return null;

    return createPortal(
        authError ? <ChatWidgetError onRetry={retry} /> : <ChatWidgetContent />,
        container
    );
};

// ── ChatWidgetError ────────────────────────────────────────────────────────────

const ChatWidgetError = ({ onRetry }: { onRetry: () => void }) => {
    return (
        <button
            aria-label="Chat unavailable — click to retry"
            onClick={onRetry}
            className={`${styles.launcher} ${styles.launcherError}`}
        >
            <ErrorOutlineIcon className={styles.launcherIcon} />
        </button>
    );
};

// ── ChatWidgetContent ──────────────────────────────────────────────────────────

const ChatWidgetContent = () => {
    // ── State ──────────────────────────────────────────────────────────────────

    const [isOpen, setIsOpen] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);

    // ── Hooks ──────────────────────────────────────────────────────────────────

    useEscapeKey(
        isOpen,
        useCallback(() => setIsOpen(false), [])
    );

    // ── Handlers ───────────────────────────────────────────────────────────────

    const handleOpen = () => setIsOpen(true);
    const handleClose = () => setIsOpen(false);
    const handleMaximize = () => setIsMaximized(m => !m);

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <>
            {/* Backdrop — dims the viewport when the panel is open, click to close */}
            <Backdrop
                open={isOpen && isMaximized}
                onClick={handleClose}
                className={styles.backdrop}
            />

            {/* ChatPanel is always mounted so ChatBody never loses its state.
                Visibility is toggled via opacity/visibility/transform animations. */}
            <ChatPanel
                isVisible={isOpen}
                isMaximized={isMaximized}
                onClose={handleClose}
                onMaximize={handleMaximize}
            />

            {/* Launcher — shown only when the panel is closed */}
            {!isOpen && (
                <button
                    aria-label="Open chat"
                    onClick={handleOpen}
                    className={styles.launcher}
                >
                    <HeadsetMicIcon className={styles.launcherIcon} />
                </button>
            )}
        </>
    );
};
